// Discover — helper components (extracted from Discover.jsx during the Feb 2026 refactor).
// These are all the presentational sub-components used by the main Discover orchestrator:
//   ActionBtn, CardStill, SwipeCard, CardBg, EmptyDeck, DeckSkeleton, MatchModal,
//   Avatar, NoteBeforeMatch, DesktopDetailsPane.
// Each is exported so Discover.jsx can compose them without re-declaring.

import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { Crown as CrownIcon, BadgeCheck, Heart, Loader2, MessageSquareText, Play, Rewind, RotateCcw, Send, ShieldOff, Sparkles, Video, Volume2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Tappable, useLightbox } from "@/context/LightboxContext";
import { api, fileUrl } from "@/lib/api";
import Logo from "@/components/app/Logo";

const TOUR_BANNER_DISMISS_KEY = "spark_tour_banner_dismissed_at";
const TOUR_BANNER_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14-day cooldown

/**
 * TourBanner — compact "Watch the 2-min tour" pill for the Discover page.
 *
 * Sits directly above the swipe deck so every logged-in user (including
 * Google Play testers signed in as demo personas) has one-tap access to
 * the full narrated /tour-pro walkthrough at any time. Opens the same
 * fullscreen modal player used on the landing/login pages.
 *
 * Dismissible: users who've watched it get a subtle × to hide it for 14
 * days. localStorage-persisted so it doesn't nag every session.
 */
export function TourBanner() {
  const [visible, setVisible] = useState(() => {
    try {
      const dismissedAt = Number(localStorage.getItem(TOUR_BANNER_DISMISS_KEY) || 0);
      return !dismissedAt || Date.now() - dismissedAt >= TOUR_BANNER_COOLDOWN_MS;
    } catch { return true; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = original; window.removeEventListener("keydown", onKey); };
  }, [open]);

  const dismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(TOUR_BANNER_DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}
        data-testid="discover-tour-banner"
        className="group relative w-full flex items-center gap-3 rounded-2xl mb-3 px-3.5 py-3 bg-gradient-to-r from-[#1B1B1E] to-[#181214] border border-[#C9A961]/25 hover:border-[#C9A961]/45 active:scale-[0.99] transition text-left overflow-hidden cursor-pointer"
      >
        <div className="shrink-0 w-9 h-9 rounded-full bg-[#C9A961] flex items-center justify-center shadow-[0_10px_28px_-10px_rgba(201,169,97,0.55)]">
          <Play className="w-3.5 h-3.5 text-[#16171A] ml-0.5" fill="currentColor" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[15px] font-medium tracking-tight text-[#F0E6D2] leading-tight">
            Watch the 2-min tour
          </div>
          <div className="flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase text-[#8C867C] mt-0.5">
            <Volume2 className="w-2.5 h-2.5" />
            <span>Narrated · every Spark feature</span>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hide tour banner"
          data-testid="discover-tour-banner-dismiss"
          className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[#8C867C] hover:text-[#F0E6D2] hover:bg-white/[0.06] transition"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[999] bg-[#0F1012]"
            data-testid="discover-tour-modal"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="discover-tour-modal-close"
              aria-label="Close tour"
              className="absolute top-4 right-4 z-[1001] w-10 h-10 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-[#F0E6D2] hover:bg-black/80 active:scale-90 transition"
            >
              <X className="w-5 h-5" />
            </button>
            <iframe
              src="/tour-pro"
              title="Spark product tour"
              className="w-full h-full border-0 block"
              allow="autoplay; fullscreen; encrypted-media"
              data-testid="discover-tour-iframe"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const VIDEO_NUDGE_DISMISS_KEY = "spark_video_nudge_dismissed_at";
const NUDGE_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days between reappearances

/**
 * Profile-completion nudges — shown one at a time on Discover, in priority
 * order (video → verify → prompts). Each is dismissible with its own 7-day
 * localStorage cooldown, so users are never nagged by more than one card
 * per session.
 */
const NUDGES = [
  {
    id: "video",
    dismissKey: VIDEO_NUDGE_DISMISS_KEY,
    eligible: (u) => !u.video_bio,
    icon: Video,
    title: "Add a 15-second video bio.",
    sub: "Profiles with video get noticeably more sparks — it\u2019s the fastest way to stand out.",
    cta: "Add video",
    to: "/profile#video-bio",
    testid: "video-bio-nudge",
  },
  {
    id: "verify",
    dismissKey: "spark_verify_nudge_dismissed_at",
    eligible: (u) => !u.verified,
    icon: BadgeCheck,
    title: "Get verified.",
    sub: "A quick selfie earns you the check — verified profiles get more matches and more trust.",
    cta: "Verify me",
    to: "/verify",
    testid: "verify-nudge",
  },
  {
    id: "prompts",
    dismissKey: "spark_prompts_nudge_dismissed_at",
    eligible: (u) => !(u.prompts && u.prompts.length),
    icon: MessageSquareText,
    title: "Answer a prompt or two.",
    sub: "Prompts show right on your swipe card — the easiest icebreaker you\u2019ll ever write.",
    cta: "Add prompts",
    to: "/profile#prompts",
    testid: "prompts-nudge",
  },
];

function nudgeDismissed(key) {
  try {
    const at = Number(localStorage.getItem(key) || 0);
    return at && Date.now() - at < NUDGE_COOLDOWN_MS;
  } catch { return false; }
}

export function ProfileNudges({ user }) {
  const [visible, setVisible] = useState(true);
  const nudge = useMemo(
    () => (user ? NUDGES.find((n) => n.eligible(user) && !nudgeDismissed(n.dismissKey)) : null),
    [user]
  );
  if (!nudge) return null;
  const Icon = nudge.icon;
  const handleDismiss = () => {
    setVisible(false);
    try { localStorage.setItem(nudge.dismissKey, String(Date.now())); } catch { /* ignore */ }
  };
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8, transition: { duration: 0.2 } }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="relative mb-3 rounded-2xl overflow-hidden bg-gradient-to-br from-[#1B1B1E] to-[#181214] border border-[#C9A961]/25 shadow-[0_8px_28px_-14px_rgba(201,169,97,0.35)]"
          data-testid={nudge.testid}
        >
          {/* Subtle diagonal shimmer accent — hand-drawn feel */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{
            backgroundImage: "linear-gradient(115deg, transparent 45%, #F0E6D2 50%, transparent 55%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 6s ease-in-out infinite",
          }} />
          <div className="relative flex items-center gap-3 p-3.5">
            <div className="w-11 h-11 rounded-xl bg-[#C9A961]/12 border border-[#C9A961]/30 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-[#C9A961]" strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-[15px] font-medium tracking-tight text-[#F0E6D2] leading-tight">
                {nudge.title}
              </div>
              <div className="text-[11px] text-[#8C867C] mt-0.5 tracking-wide leading-relaxed">
                {nudge.sub}
              </div>
            </div>
            <Link
              to={nudge.to}
              className="shrink-0 text-[10px] font-bold tracking-[0.12em] uppercase px-3 py-2 rounded-full bg-[#C9A961] text-[#0F1012] hover:bg-[#D4B571] active:scale-95 transition"
              data-testid={`${nudge.testid}-cta`}
            >
              {nudge.cta}
            </Link>
            <button
              onClick={handleDismiss}
              className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[#8C867C] hover:text-[#F0E6D2] hover:bg-white/[0.06] transition"
              aria-label="Dismiss"
              data-testid={`${nudge.testid}-dismiss`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function ActionBtn({ children, color, textColor, onClick, big, small, testid, tooltip, label }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <motion.button
        onClick={onClick}
        data-testid={testid}
        title={tooltip}
        aria-label={tooltip}
        whileTap={{ scale: 0.88 }}
        whileHover={{ scale: 1.06 }}
        transition={{ type: "spring", stiffness: 500, damping: 22 }}
        className={`rounded-full flex items-center justify-center will-change-transform ${
          big ? "w-[72px] h-[72px]" : small ? "w-10 h-10" : "w-11 h-11"
        }`}
        style={{
          backgroundColor: color,
          color: textColor,
          boxShadow: big
            ? `0 0 30px ${color}55, 0 14px 30px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)`
            : `0 10px 24px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)`,
          border: "1px solid rgba(240,230,210,0.06)",
        }}
      >
        {children}
      </motion.button>
      {label && (
        <span className="hidden lg:block text-[10px] uppercase tracking-[0.18em] text-[#8C867C] font-medium">
          {label}
        </span>
      )}
    </div>
  );
}

export function CardStill({ user }) {
  return (
    <motion.div
      className="absolute inset-0 rounded-[28px] overflow-hidden bg-[#1E1F23] border border-[#10B981]/40"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      style={{
        zIndex: 0,
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(16,185,129,0.18), inset 0 0 0 1px rgba(201,169,97,0.04)",
      }}
    >
      <CardBg user={user} />
    </motion.div>
  );
}

export function SwipeCard({ user, onSwipe, onAskInsight, insight, onSafety }) {
  const { t } = useTranslation();
  const { openLightbox } = useLightbox();
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-18, 18]);
  const likeOpacity = useTransform(x, [40, 140], [0, 1]);
  const passOpacity = useTransform(x, [-140, -40], [1, 0]);
  const [showVideo, setShowVideo] = React.useState(false);

  return (
    <motion.div
      className="absolute inset-0 rounded-[28px] overflow-hidden bg-[#1E1F23] cursor-zoom-in active:cursor-grabbing border border-[#10B981]/40"
      style={{
        x,
        rotate,
        zIndex: 1,
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(16,185,129,0.18), 0 0 40px -8px rgba(16,185,129,0.22), inset 0 0 0 1px rgba(201,169,97,0.04)",
      }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={(_, info) => {
        if (info.offset.x > 120) onSwipe("like", user);
        else if (info.offset.x < -120) onSwipe("pass", user);
      }}
      // Tap-to-magnify: Framer's onTap fires only on quick taps (not drags),
      // so it cleanly coexists with the swipe-to-decide gesture.
      onTap={() => {
        const photos = (user.photos || []).map((p) => fileUrl(p.storage_path));
        if (photos.length > 0) openLightbox(photos, 0);
      }}
      initial={{ scale: 1 }}
      // Only slide off if the card was actually dragged (a real swipe).
      // Skips and rewinds don't move x, so we bail out instantly — that
      // keeps the photo perfectly in sync with the name/bio when you click
      // Rewind multiple times quickly. (Old version slid the card off-screen
      // every time it unmounted, which let the photo lag behind the data.)
      exit={x.get() !== 0
        ? { x: x.get() > 0 ? 600 : -600, opacity: 0, transition: { duration: 0.25 } }
        : { opacity: 0, transition: { duration: 0 } }
      }
    >
      <CardBg user={user} />

      {/* Video bio badge */}
      {user.video_bio && (
        <button
          onClick={(e) => { e.stopPropagation(); setShowVideo(true); }}
          className="absolute top-4 left-4 z-20 bg-[#B95F73]/95 backdrop-blur-md text-[#F0E6D2] rounded-full pl-2 pr-3 py-1.5 text-[11px] font-bold flex items-center gap-1.5 active:scale-95 shadow-xl shadow-[#B95F73]/40"
          data-testid="play-video-bio"
        >
          <span className="w-5 h-5 rounded-full bg-[#F0E6D2] text-[#B95F73] flex items-center justify-center">
            <Play className="w-2.5 h-2.5 ml-0.5" fill="currentColor" />
          </span>
          VIDEO BIO
        </button>
      )}

      {/* Pre-match note (Spark+ feature — they swiped you with a message) */}
      {user.pre_message && (
        <div
          className="absolute top-16 left-4 right-4 z-20 rounded-2xl p-3 backdrop-blur-md"
          style={{
            background: "linear-gradient(135deg, rgba(201,169,97,0.92), rgba(142,111,58,0.92))",
            boxShadow: "0 12px 30px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.18)",
          }}
          data-testid="pre-match-note"
        >
          <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.2em] text-[#16171A] mb-1">
            <Sparkles className="w-2.5 h-2.5" fill="currentColor" />
            They left you a note
          </div>
          <p className="text-sm font-display italic leading-snug text-[#16171A]">
            "{user.pre_message}"
          </p>
        </div>
      )}
      {showVideo && user.video_bio && (
        <div
          className="absolute inset-0 bg-black z-30 flex items-center justify-center"
          onClick={(e) => { e.stopPropagation(); setShowVideo(false); }}
          data-testid="video-bio-overlay"
        >
          <video
            src={fileUrl(user.video_bio.storage_path)}
            autoPlay
            playsInline
            controls
            className="w-full h-full object-contain"
            onEnded={() => setShowVideo(false)}
          />
          <button
            onClick={(e) => { e.stopPropagation(); setShowVideo(false); }}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/70 text-[#F0E6D2] flex items-center justify-center z-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Decision badges */}
      <motion.div
        className="absolute top-10 left-6 px-4 py-2.5 rounded-2xl bg-[#B95F73]/95 backdrop-blur-md text-[#F0E6D2] font-black text-2xl tracking-tighter -rotate-[14deg] shadow-2xl ring-2 ring-[#F0E6D2]/30"
        style={{ opacity: likeOpacity }}
      >
        ♥ SPARK
      </motion.div>
      <motion.div
        className="absolute top-10 right-6 px-4 py-2.5 rounded-2xl bg-[#16171A]/95 backdrop-blur-md text-[#F0E6D2] font-black text-2xl tracking-tighter rotate-[14deg] shadow-2xl ring-2 ring-[#F0E6D2]/30"
        style={{ opacity: passOpacity }}
      >
        NOPE
      </motion.div>

      {/* AI insight */}
      <div className="absolute top-4 right-4 left-4 flex justify-end">
        <button
          onClick={onAskInsight}
          className="bg-[#C9A961] text-[#F0E6D2] rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1 active:scale-95"
          data-testid="ai-insight-btn"
        >
          <Sparkles className="w-3 h-3" fill="currentColor" />
          {insight && insight !== "..." ? "AI vibe" : "AI vibe check"}
        </button>
      </div>
      {insight && (
        <div className="absolute top-14 right-4 left-4 flex justify-end">
          <div className="bg-[#1B1B1E]/95 backdrop-blur-md rounded-2xl px-4 py-2 text-xs max-w-[80%] text-right border border-[#2A2A2F]" data-testid="ai-insight">
            {insight === "..." ? <Loader2 className="w-3 h-3 animate-spin inline" /> : insight}
          </div>
        </div>
      )}

      {/* Bottom info */}
      <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black via-black/70 to-transparent text-[#F0E6D2]">
        <div className="flex items-end gap-2 mb-2 lg:mb-0">
          <h2 className="font-display text-[44px] lg:text-[32px] font-light tracking-tight leading-none">{user.name}</h2>
          <span className="font-display text-2xl font-light opacity-70 pb-1">{user.age}</span>
          {user.mode === "friends" && (
            <span className="ml-1 text-[9px] uppercase tracking-widest bg-[#10B981] text-[#F0E6D2] rounded-full px-2 py-0.5 mb-2 font-bold">
              Friends only
            </span>
          )}
        </div>
        {user.vibe_status && (
          <div className="lg:hidden inline-flex items-center gap-1.5 bg-[#C9A961]/95 text-[#16171A] rounded-full px-2.5 py-1 mb-2 text-[11px] font-bold">
            <span className="w-1.5 h-1.5 bg-[#16171A] rounded-full animate-pulse" />
            {user.vibe_status}
          </div>
        )}
        {user.bio && <p className="lg:hidden text-sm leading-snug opacity-95 line-clamp-2 mb-2">{user.bio}</p>}
        {user.prompts?.[0]?.a && (
          <div className="lg:hidden bg-[#16171A]/85 backdrop-blur-md border border-[#F0E6D2]/10 text-[#F0E6D2] rounded-2xl px-3.5 py-2.5 mb-2">
            <div className="text-[9px] uppercase tracking-widest font-bold text-[#B95F73] mb-0.5">{user.prompts[0].q}</div>
            <div className="text-sm font-display font-medium leading-snug line-clamp-2 italic">"{user.prompts[0].a}"</div>
          </div>
        )}
        {user.interests?.length > 0 && (
          <div className="lg:hidden flex flex-wrap gap-1.5">
            {user.interests.slice(0, 4).map((i) => (
              <span key={i} className="px-2.5 py-1 rounded-full bg-[#F0E6D2]/15 backdrop-blur-md text-[#F0E6D2] text-[10px] font-bold uppercase tracking-wider border border-[#F0E6D2]/10">
                {i}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function CardBg({ user }) {
  const photo = user.photos?.[0];
  if (photo) {
    return (
      <Tappable
        loading="eager"
        decoding="sync"
        fetchPriority="high"
        src={fileUrl(photo.storage_path)}
        photos={(user.photos || []).map((p) => fileUrl(p.storage_path))}
        alt={user.name}
        className="w-full h-full object-cover"
        style={{ objectPosition: "center 20%" }}
        testid={`swipe-card-photo-${user.id}`}
      />
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#C9A961] to-[#B95F73]">
      <div className="font-display text-[120px] font-black text-[#F0E6D2]/90 leading-none">
        {user.name?.[0] || "?"}
      </div>
    </div>
  );
}

export function EmptyDeck({ onRewind }) {
  const { t } = useTranslation();
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
      <motion.div
        className="w-20 h-20 rounded-full bg-gradient-to-br from-[#C9A961] to-[#8E6F3A] flex items-center justify-center mb-5 shadow-2xl shadow-[#C9A961]/30"
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <Sparkles className="w-8 h-8 text-[#16171A]" fill="currentColor" />
      </motion.div>
      <div className="font-display italic text-[10px] tracking-widest uppercase text-[#C9A961] mb-2">— Pause</div>
      <h3 className="font-display text-3xl font-light tracking-tight mb-2 leading-[1.05]">
        {t("discover.no_more")}
      </h3>
      <p className="text-sm text-[#C8C2B5] max-w-[28ch] leading-relaxed mb-6">
        {t("discover.come_back")}
      </p>
      {onRewind && (
        <button
          onClick={onRewind}
          className="mb-3 px-6 py-3 rounded-full bg-gradient-to-r from-[#C9A961] to-[#8E6F3A] text-[#16171A] text-xs font-bold tracking-widest uppercase active:scale-95 transition flex items-center gap-2 shadow-lg shadow-[#C9A961]/25"
          data-testid="empty-deck-rewind"
        >
          <RotateCcw className="w-4 h-4" />
          Bring back last profile
        </button>
      )}
      <div className="flex items-center gap-2">
        <a
          href="/groups"
          className="px-5 py-2.5 rounded-full bg-[#F0E6D2] text-[#16171A] text-xs font-bold tracking-widest uppercase active:scale-95 transition"
          data-testid="empty-deck-groups"
        >
          {t("nav.groups")}
        </a>
        <a
          href="/referrals"
          className="px-5 py-2.5 rounded-full border border-[#C9A961]/40 text-[#C9A961] text-xs font-bold tracking-widest uppercase active:scale-95 transition"
          data-testid="empty-deck-invite"
        >
          Invite
        </a>
      </div>
    </div>
  );
}

export function DeckSkeleton() {
  // Shimmer skeleton mimicking a swipe card — feels intentional vs a spinner.
  return (
    <div className="absolute inset-0">
      <div className="absolute inset-0 rounded-[28px] bg-[#1E1F23] overflow-hidden border border-[#2A2A2F]">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            background:
              "linear-gradient(110deg, transparent 0%, transparent 35%, rgba(240,230,210,0.05) 50%, transparent 65%, transparent 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.6s linear infinite",
          }}
        />
        <div className="absolute bottom-0 left-0 right-0 p-6 space-y-2">
          <div className="h-3 w-32 rounded-full bg-[#2A2A2F]" />
          <div className="h-7 w-48 rounded-full bg-[#2A2A2F]" />
          <div className="h-2 w-24 rounded-full bg-[#2A2A2F]" />
        </div>
      </div>
      <style>{`@keyframes shimmer { from { background-position: 200% 0 } to { background-position: -200% 0 } }`}</style>
    </div>
  );
}

export function MatchModal({ matchUser, me, onClose, onChat }) {
  // 18 confetti particles drifting outward with random rotation/colour
  const palette = ["#C9A961", "#F0E6D2", "#E8C77A", "#F5E1B8"];
  const particles = React.useMemo(
    () => Array.from({ length: 18 }).map((_, i) => ({
      x: (Math.random() - 0.5) * 400,
      y: (Math.random() - 0.7) * 600,
      rot: Math.random() * 540 - 270,
      delay: i * 0.025,
      colour: palette[i % palette.length],
      size: 6 + Math.random() * 8,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center text-[#F0E6D2] px-8 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at center, #C24661 0%, #8E2E48 55%, #4A152A 100%)",
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-testid="match-modal"
    >
      {/* Confetti */}
      {particles.map((p, i) => (
        <motion.div
          key={i}
          className="absolute top-1/2 left-1/2 rounded-sm pointer-events-none"
          style={{
            width: p.size, height: p.size, backgroundColor: p.colour,
            boxShadow: `0 0 12px ${p.colour}80`,
          }}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
          animate={{ x: p.x, y: p.y, opacity: 0, rotate: p.rot }}
          transition={{ duration: 1.6, delay: p.delay, ease: "easeOut" }}
        />
      ))}

      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 18 }}
      >
        <Logo size={36} className="mb-4" glow />
      </motion.div>

      <motion.div
        className="font-display italic text-[10px] tracking-[0.32em] uppercase text-[#F0E6D2]/70 mb-2"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        — A spark just struck —
      </motion.div>

      <motion.h1
        className="font-display text-[3.5rem] leading-[0.95] font-light tracking-tight mb-2 text-center"
        initial={{ scale: 0.7, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 16, delay: 0.15 }}
      >
        It's a <span className="italic text-[#F5E1B8]">spark!</span>
      </motion.h1>

      <motion.p
        className="text-[15px] opacity-90 mb-9 font-display tracking-tight"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.9 }}
        transition={{ delay: 0.35 }}
      >
        You and {matchUser.name} liked each other.
      </motion.p>

      <div className="flex items-center -space-x-7 mb-10 relative">
        <motion.div
          initial={{ x: -60, opacity: 0, scale: 0.7 }}
          animate={{ x: 0, opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.2 }}
        >
          <Avatar user={me} ring />
        </motion.div>
        <motion.div
          initial={{ x: 60, opacity: 0, scale: 0.7 }}
          animate={{ x: 0, opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.28 }}
        >
          <Avatar user={matchUser} ring />
        </motion.div>
        {/* Heart that pulses between them */}
        <motion.div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.35, 1] }}
          transition={{ delay: 0.55, duration: 0.5, times: [0, 0.6, 1] }}
        >
          <div className="w-14 h-14 rounded-full bg-[#F0E6D2] flex items-center justify-center shadow-2xl">
            <Heart className="w-7 h-7 text-[#B95F73]" fill="currentColor" />
          </div>
        </motion.div>
      </div>

      <motion.button
        onClick={onChat}
        className="w-full max-w-xs bg-[#F0E6D2] text-[#1B1B1E] rounded-full py-4 font-bold mb-3 active:scale-95 transition shadow-2xl shadow-black/30"
        data-testid="match-start-chat"
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        Say hi to {matchUser.name?.split(" ")[0]}
      </motion.button>
      <motion.button
        onClick={onClose}
        className="text-sm font-display italic opacity-75 underline underline-offset-4"
        data-testid="match-keep-swiping"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.75 }}
        transition={{ delay: 0.85 }}
      >
        Keep swiping
      </motion.button>
    </motion.div>
  );
}

export function Avatar({ user, ring }) {
  const photo = user?.photos?.[0];
  return (
    <div
      className={`w-32 h-32 rounded-full overflow-hidden bg-[#1B1B1E]/20 flex items-center justify-center ${
        ring ? "ring-4 ring-[#F0E6D2]" : ""
      }`}
    >
      {photo ? (
        <Tappable loading="lazy" decoding="async" src={fileUrl(photo.storage_path)} photos={(user.photos || []).map((p) => fileUrl(p.storage_path))} alt="" className="w-full h-full object-cover" testid={`match-avatar-${user?.id}`} />
      ) : (
        <span className="font-display text-5xl font-black">{user?.name?.[0]}</span>
      )}
    </div>
  );
}

export function NoteBeforeMatch({ target, text, onChange, onClose, onSkip, onSend, busy }) {
  const { t } = useTranslation();
  const remaining = 240 - (text?.length || 0);
  return (
    <motion.div
      className="fixed inset-0 z-50 bg-[#16171A]/85 backdrop-blur-md flex items-end sm:items-center justify-center px-4 pb-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-testid="note-before-match"
    >
      <motion.div
        className="w-full max-w-md bg-[#1E1F23] rounded-[28px] border border-[#F0E6D2]/8 overflow-hidden"
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
        style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.7)" }}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-[#C9A961] to-[#8E6F3A] shadow-md shadow-[#C9A961]/40">
              <CrownIcon className="w-3.5 h-3.5 text-[#16171A]" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C9A961]">{t("discover.spark_plus_pre_message")}</span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#1B1B1E] border border-[#F0E6D2]/10 flex items-center justify-center active:scale-90"
            data-testid="note-close"
          >
            <X className="w-3.5 h-3.5 text-[#8C867C]" />
          </button>
        </div>
        <div className="px-5 pb-2">
          <div className="font-display text-[28px] font-light tracking-tight leading-tight">
            Send {target?.name} a note
          </div>
          <p className="text-xs text-[#8C867C] mt-1 mb-3">
            They'll see it on your card. Lead with curiosity — keep it under 240 characters.
          </p>
          <textarea
            value={text}
            onChange={(e) => onChange(e.target.value.slice(0, 240))}
            placeholder={`Hey ${target?.name?.split(" ")[0] || "there"} — I noticed you love ${target?.interests?.[0] || "the same things I do"}. Best spot in ${target?.city || "town"}?`}
            rows={3}
            autoFocus
            className="w-full bg-[#1B1B1E] border border-[#F0E6D2]/10 rounded-2xl px-4 py-3 outline-none focus:border-[#C9A961]/50 text-[15px] leading-snug resize-none placeholder:text-[#5C5750]"
            data-testid="note-textarea"
          />
          <div className="flex justify-between items-center mt-2 mb-4">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#8C867C]">
              {remaining} left
            </span>
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#C9A961]">
              {text?.trim() ? "Sparkles attached" : "Optional"}
            </span>
          </div>
        </div>
        <div className="flex items-stretch gap-2 px-4 pb-4">
          <button
            onClick={onSkip}
            disabled={busy}
            className="flex-1 bg-transparent border border-[#F0E6D2]/12 rounded-full py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8C867C] active:scale-95"
            data-testid="note-skip"
          >
            Skip — just like
          </button>
          <button
            onClick={onSend}
            disabled={busy}
            className="flex-1 bg-[#C9A961] text-[#16171A] rounded-full py-3 text-[11px] font-semibold uppercase tracking-[0.2em] flex items-center justify-center gap-2 active:scale-95 disabled:opacity-60"
            style={{ boxShadow: "0 0 24px rgba(201,169,97,0.35), inset 0 1px 0 rgba(255,255,255,0.12)" }}
            data-testid="note-send"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send & Spark
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}


/**
 * DesktopDetailsPane — the right-side profile detail panel on lg+ Discover.
 *
 * Shown only on lg (≥1024px). On mobile/tablet the swipe card already shows
 * everything inline, so this pane is `hidden`. It uses the same data the
 * swipe card consumes (current `top` user) plus an optional AI insight blurb.
 */
export function DesktopDetailsPane({ user, insight, onAskInsight, onReport }) {
  if (!user) return null;
  const photos = (user.photos || []).slice(0, 4);
  const prompts = (user.prompts || []).slice(0, 3);
  const interests = user.interests || [];

  return (
    <div
      data-testid="discover-details-pane"
      className="hidden lg:flex lg:flex-1 lg:min-w-0 lg:flex-col lg:gap-4 lg:pt-1"
    >
      {/* Name + meta + report (top-right, subtle) */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <h2
              className="text-[40px] tracking-[-0.02em] text-[#F0E6D2] leading-none"
              style={{ fontFamily: '"Fraunces", "Cormorant Garamond", serif', fontWeight: 400 }}
            >
              {user.name}
            </h2>
            <span className="text-2xl font-light text-[#A0A0A8]">{user.age}</span>
            {user.verified && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#8FE0C5]">
                <Sparkles className="w-3 h-3" fill="currentColor" />
                Verified
              </span>
            )}
          </div>
          {(user.city || user.distance_km != null) && (
            <div className="text-[12px] text-[#8C867C] mt-1.5 tracking-wide">
              {user.city || "Nearby"}
              {user.distance_km != null && ` · ${Math.round(user.distance_km)} km away`}
            </div>
          )}
          {user.vibe_status && (
            <div className="mt-2 text-[13px] text-[#C9A961] italic" style={{ fontFamily: '"Fraunces", serif' }}>
              ✦ {user.vibe_status}
            </div>
          )}
        </div>
        {onReport && (
          <button
            onClick={onReport}
            data-testid="details-report-btn"
            title={`Report ${user.name}`}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9.5px] uppercase tracking-[0.2em] text-[#6E6A62] hover:text-[#B95F73] hover:bg-[#B95F73]/8 border border-white/[0.06] hover:border-[#B95F73]/30 transition-colors active:scale-95"
          >
            <ShieldOff className="w-3 h-3" strokeWidth={1.8} />
            Report
          </button>
        )}
      </div>

      {/* AI insight — moved to the top so it's always above the fold on
          laptop viewports (was previously at the bottom of the pane). */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={onAskInsight}
          disabled={insight === "..."}
          className="rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-medium bg-gradient-to-r from-[#C9A961] to-[#8E6F3A] text-[#16171A] active:scale-95 disabled:opacity-60"
          data-testid="details-ask-insight"
        >
          {insight === "..." ? "Reading..." : "✦ Ask AI for compatibility"}
        </button>
        {insight && insight !== "..." && (
          <div className="flex-1 min-w-0 rounded-2xl bg-[#C9A961]/10 border border-[#C9A961]/25 px-4 py-2 text-[12.5px] text-[#F0E6D2]/95 leading-relaxed">
            {insight}
          </div>
        )}
      </div>

      {/* Bio (full, not truncated) */}
      {user.bio && (
        <p className="text-[14px] text-[#F0E6D2]/90 leading-relaxed">
          {user.bio}
        </p>
      )}

      {/* Prompts */}
      {prompts.filter((p) => p?.a?.trim()).length > 0 && (
        <div className="space-y-2.5">
          {prompts
            .filter((p) => p?.a?.trim())
            .map((p, i) => (
            <div
              key={i}
              className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3"
            >
              <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-[#B95F73] mb-1">
                {p.q}
              </div>
              <div
                className="text-[15px] text-[#F0E6D2] leading-snug"
                style={{ fontFamily: '"Fraunces", serif', fontStyle: "italic", fontWeight: 400 }}
              >
                "{p.a}"
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Interests */}
      {interests.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {interests.slice(0, 8).map((i) => (
            <span
              key={i}
              className="px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/[0.06] text-[11px] uppercase tracking-[0.14em] text-[#A0A0A8]"
            >
              {i}
            </span>
          ))}
        </div>
      )}

      {/* Photos grid was removed — the swipe card on the left already shows
          the main photo big, and tapping it opens the full gallery in the
          lightbox. Keeping the details pane compact so it always fits above
          the fold on laptop viewports. */}

      {/* AI insight moved to the top of the pane (see above). */}
    </div>
  );
}
